import { rufe, schluesselAblage } from './client';

export type Benutzer = {
  readonly id: string;
  readonly anmeldename: string;
  readonly name: string;
  readonly email: string;
  readonly status: 'aktiv' | 'gesperrt' | 'deaktiviert';
  /**
   * RES-28: Die dritte Kontoart ist der Onlinezugang eines Trainers oder
   * externen Dienstleisters. App.tsx schickt sie in den
   * Trainerkalender und nicht in die Arbeitsfläche (RES-30: keine
   * CRM-Navigation).
   */
  /* `pruefer` seit 0225 (BP-01): das Prueferkonto der Betriebspruefung. */
  readonly kontoart: 'person' | 'technisch' | 'trainer' | 'pruefer';
  readonly team: string | null;
  readonly sichtbarkeit: 'eigene' | 'team' | 'alle';
  /** RES-32: die Ressource hinter einem Dienstleisterkonto, sonst `null`. */
  readonly ressource: string | null;
};

export type Recht = {
  readonly objekt: string;
  readonly aktion: string;
  readonly umfang: 'eigene' | 'team' | 'alle';
  /** Woher das Recht kommt — Rolle oder `vertretung:<name>` (BEN-17). */
  readonly ausRollen: readonly string[];
  /**
   * Dieselbe Herkunft zum Anzeigen, Stelle für Stelle wie `ausRollen`: die
   * Bezeichnung der Rolle, „Vertretung für …", „Zusatzrecht" (Auftrag CRM-79).
   * Die Oberfläche zeigt nur diese Liste, nie die Kennungen.
   */
  readonly herkunft: readonly string[];
  readonly ueberVertretung: boolean;
};

export type Sitzung = {
  readonly id: string;
  /** UTC nach ISO 8601; die Anzeige rechnet in Ortszeit um (LOG-03). */
  readonly begonnenAm: string;
  readonly letzteAktivitaet: string;
  readonly ip: string | null;
  readonly geraet: string | null;
  readonly diese: boolean;
};

export type Anmeldeergebnis = {
  readonly schluessel: string;
  readonly benutzer: Benutzer;
  readonly passwortwechselFaellig: boolean;
  /**
   * SYS-05: der Schlüssel eines neu gemerkten Geräts — **einmal** und nur
   * hier. Gespeichert wird nur sein Streuwert; wer ihn verliert, merkt das
   * Gerät neu.
   */
  readonly geraeteschluessel?: string;
  /** SYS-05: bis wann das gemerkte Gerät gilt — Mitternacht, UTC nach ISO 8601. */
  readonly geraetGueltigBis?: string;
};

/** SYS-05: ein vertrauenswürdiges Gerät. */
export type Geraet = {
  readonly id: string;
  readonly bezeichnung: string;
  readonly angelegtAm: string;
  readonly angelegtIp: string | null;
  readonly letzteNutzung: string | null;
  readonly gueltigBis: string;
  readonly widerrufenAm: string | null;
  readonly widerrufenVonName: string | null;
  /** Weder widerrufen noch abgelaufen — nur dann greift es. */
  readonly gueltig: boolean;
};

/**
 * Wo der Geräteschlüssel liegt.
 *
 * `localStorage` und nicht der Sitzungsspeicher: Er soll die Sitzung
 * überleben — das ist sein ganzer Zweck. Er ist **kein** Zugang: Ohne
 * Kennwort nützt er niemandem, und er läuft nach der in der Richtlinie
 * hinterlegten Frist ab.
 */
const GERAETESCHLUESSEL = 'crm.geraet';

export const geraeteAblage = {
  lesen(): string | null {
    try {
      return globalThis.localStorage?.getItem(GERAETESCHLUESSEL) ?? null;
    } catch {
      // Ein Browser ohne Speicher (privater Modus, gesperrte Seitendaten)
      // soll die Anmeldung nicht verhindern — dann eben mit Code.
      return null;
    }
  },
  setzen(wert: string): void {
    try {
      globalThis.localStorage?.setItem(GERAETESCHLUESSEL, wert);
    } catch {
      // dito
    }
  },
  loeschen(): void {
    try {
      globalThis.localStorage?.removeItem(GERAETESCHLUESSEL);
    } catch {
      // dito
    }
  },
};

/**
 * Anmelden. Der Schluessel wandert in die Ablage, damit die Folgeanfragen
 * ihn mitnehmen — der Aufrufer muss nicht daran denken.
 */
export async function anmelden(angabe: {
  anmeldename: string;
  passwort: string;
  code?: string;
  /** SYS-05: dieses Gerät für die Dauer der Richtlinie merken. */
  geraetMerken?: boolean;
}): Promise<Anmeldeergebnis> {
  /*
   * SYS-05: Der Geräteschlüssel wird MITGESCHICKT, wenn einer vorliegt. Er
   * überspringt den zweiten Faktor — nie das Kennwort, das prüft der Server
   * unverändert.
   */
  const geraet = geraeteAblage.lesen();

  const ergebnis = await rufe<Anmeldeergebnis>('/anmeldung', {
    verfahren: 'POST',
    ohneSchluessel: true,
    rumpf: {
      anmeldename: angabe.anmeldename,
      passwort: angabe.passwort,
      ...(angabe.code !== undefined && angabe.code !== '' ? { code: angabe.code } : {}),
      ...(geraet === null ? {} : { geraeteschluessel: geraet }),
      ...(angabe.geraetMerken === true ? { geraetMerken: true } : {}),
    },
  });
  schluesselAblage.setzen(ergebnis.schluessel);
  if (ergebnis.geraeteschluessel !== undefined) {
    geraeteAblage.setzen(ergebnis.geraeteschluessel);
  }
  return ergebnis;
}

/**
 * Was der erste Schritt ergeben hat (SYS-05).
 *
 * `angemeldet` heißt: Ein gemerktes Gerät galt, der Code entfällt. Die Maske
 * zeigt dann gar kein Codefeld.
 */
export type Anmeldebeginn =
  | ({ readonly art: 'angemeldet' } & Anmeldeergebnis)
  | {
      readonly art: 'zweiter_faktor';
      readonly vorgang: string;
      readonly laeuftAbAm: string;
    };

/**
 * SCHRITT 1: Anmeldename und Kennwort (SYS-05).
 *
 * Der Geräteschlüssel wird MITGESCHICKT, wenn einer vorliegt — er überspringt
 * den zweiten Faktor, nie das Kennwort.
 *
 * OHNE „Gerät merken" (0128): Der Haken steht im zweiten Formular. Hier wäre
 * er wirkungslos, denn gilt bereits ein Gerät, gibt es gar keinen zweiten
 * Schritt — und dann wird er ausdrücklich nicht eingelöst.
 *
 * Bei `angemeldet` legt diese Funktion den Sitzungsschlüssel gleich ab; bei
 * `zweiter_faktor` gibt es noch keinen, und es wird auch keiner abgelegt.
 */
export async function schritt1(angabe: {
  anmeldename: string;
  passwort: string;
}): Promise<Anmeldebeginn> {
  const geraet = geraeteAblage.lesen();
  const ergebnis = await rufe<Anmeldebeginn>('/anmeldung/schritt1', {
    verfahren: 'POST',
    ohneSchluessel: true,
    rumpf: {
      anmeldename: angabe.anmeldename,
      passwort: angabe.passwort,
      ...(geraet === null ? {} : { geraeteschluessel: geraet }),
    },
  });

  if (ergebnis.art === 'angemeldet') {
    schluesselAblage.setzen(ergebnis.schluessel);
    if (ergebnis.geraeteschluessel !== undefined) {
      geraeteAblage.setzen(ergebnis.geraeteschluessel);
    }
  }
  return ergebnis;
}

/**
 * SCHRITT 2: der Bestätigungscode — und „Gerät merken" (SYS-05, 0128).
 *
 * Der Vorgang wird beim Einlösen verbraucht — auch bei falschem Code. Wer
 * sich vertippt, beginnt von vorn; das ist der Preis dafür, dass sechs
 * Ziffern nicht beliebig oft geraten werden können.
 *
 * Der Haken reist hier mit und nicht im ersten Schritt: Diesen Schritt
 * erreicht nur, wer kein geltendes Gerät vorzuzeigen hat — also genau der,
 * für den die Frage eine ist.
 */
export async function schritt2(angabe: {
  vorgang: string;
  code: string;
  geraetMerken?: boolean;
}): Promise<Anmeldeergebnis> {
  const ergebnis = await rufe<Anmeldeergebnis>('/anmeldung/schritt2', {
    verfahren: 'POST',
    ohneSchluessel: true,
    rumpf: {
      vorgang: angabe.vorgang,
      code: angabe.code,
      ...(angabe.geraetMerken === true ? { geraetMerken: true } : {}),
    },
  });
  schluesselAblage.setzen(ergebnis.schluessel);
  if (ergebnis.geraeteschluessel !== undefined) {
    geraeteAblage.setzen(ergebnis.geraeteschluessel);
  }
  return ergebnis;
}

/** SYS-05: die eigenen vertrauenswürdigen Geräte — auch die widerrufenen. */
export async function geraete(): Promise<readonly Geraet[]> {
  return rufe('/anmeldung/geraete');
}

export async function geraetWiderrufen(id: string): Promise<void> {
  await rufe<void>(`/anmeldung/geraete/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

export async function abmelden(): Promise<void> {
  try {
    await rufe<void>('/anmeldung/abmelden', { verfahren: 'POST' });
  } finally {
    // Auch wenn der Aufruf scheitert: Der Schluessel gehoert lokal weg. Sonst
    // sieht der Anwender sich abgemeldet und ist es nicht.
    schluesselAblage.loeschen();
  }
}

export async function ich(): Promise<{ benutzer: Benutzer; rechte: readonly Recht[] }> {
  return rufe('/anmeldung/ich');
}

/**
 * CRM-153: Firmierung und Kontakt aus den Geschäftsdaten für die Anmeldeseite.
 * Ohne Anmeldung; `null` heißt „nicht gepflegt".
 */
export type Hauskopf = {
  readonly firmierung: string | null;
  readonly email: string | null;
  readonly telefon: string | null;
};

export async function hauskopf(): Promise<Hauskopf> {
  return rufe('/anmeldung/hauskopf', { ohneSchluessel: true });
}

export async function sitzungen(): Promise<{ sitzungen: readonly Sitzung[] }> {
  return rufe('/anmeldung/sitzungen');
}

export async function sitzungBeenden(id: string): Promise<void> {
  await rufe<void>(`/anmeldung/sitzungen/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

export async function passwortWechseln(angabe: {
  altesPasswort: string;
  neuesPasswort: string;
}): Promise<void> {
  await rufe<void>('/anmeldung/passwort', { verfahren: 'POST', rumpf: angabe });
}

/**
 * Das eigene Kennwort aus freien Stücken ändern — „Mein Konto“ (AeA-48).
 * Die eigene Sitzung bleibt; die Antwort nennt, wie viele andere enden.
 */
export async function kennwortAendern(angabe: {
  altesPasswort: string;
  neuesPasswort: string;
}): Promise<{ readonly beendeteSitzungen: number }> {
  return rufe('/anmeldung/kennwort', { verfahren: 'POST', rumpf: angabe });
}

/** AeA-48: der eigene Kennwortstand. Zeitpunkte in UTC nach ISO 8601. */
export type Kennwortstand = {
  readonly gesetztAm: string;
  /** `null`: Das Kennwort läuft nicht ab. */
  readonly laeuftAbAm: string | null;
  /** Ablauf in höchstens 14 Tagen. */
  readonly vorwarnung: boolean;
};

export async function kennwortstand(): Promise<Kennwortstand> {
  return rufe<Kennwortstand>('/anmeldung/kennwort');
}

/**
 * SYS-05: Einrichtung des zweiten Faktors beginnen.
 *
 * Ohne Sitzung — es gibt noch keine. Geschuetzt ist der Weg durch Anmeldename
 * und Passwort, und dadurch, dass er einen bereits bestaetigten Faktor nicht
 * ersetzt.
 */
export async function zweitenFaktorBeginnen(angabe: {
  anmeldename: string;
  passwort: string;
}): Promise<{ geheimnis: string; adresse: string }> {
  return rufe('/anmeldung/zweiter-faktor', {
    verfahren: 'POST',
    ohneSchluessel: true,
    rumpf: angabe,
  });
}

export async function zweitenFaktorBestaetigen(angabe: {
  anmeldename: string;
  passwort: string;
  code: string;
}): Promise<void> {
  await rufe<void>('/anmeldung/zweiter-faktor/bestaetigen', {
    verfahren: 'POST',
    ohneSchluessel: true,
    rumpf: angabe,
  });
}

/**
 * Die Herkunft eines Rechts als Paare aus Kennung und Anzeigename (BEN-17).
 *
 * Die Kennung entscheidet (Rolle, `vertretung:…`, `zusatzrecht`), der Name
 * steht auf dem Bildschirm (Auftrag CRM-79). Fehlt ein Name, bleibt die
 * Kennung sichtbar — eine Lücke soll auffallen und nicht leer sein.
 */
export function herkunftVon(recht: Recht): readonly { kennung: string; name: string }[] {
  return recht.ausRollen.map((kennung, i) => ({ kennung, name: recht.herkunft[i] ?? kennung }));
}
